import React, { useState, useMemo } from 'react';
import {
  Lightbulb, TrendingUp, Shield, Clock, Users, Zap,
  ChevronRight, ThumbsUp, ThumbsDown, X, Check, Eye,
  AlertTriangle, ArrowRight, Play, Bookmark, Filter,
  RefreshCw, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';

const mockSuggestions = [
  {
    id: 'sug_001',
    type: 'automation',
    title: 'Automate password reset requests',
    description: 'Based on 47 similar tickets this month, password resets for standard users could be fully automated with self-service.',
    impact: {
      timeSaved: '12 hours/week',
      ticketsAffected: 47,
      costSavings: '$2,400/month'
    },
    confidence: 94,
    evidence: [
      '47 password reset tickets in last 30 days',
      '98% followed same resolution pattern',
      'Average resolution time: 15 minutes',
      'No security exceptions required'
    ],
    status: 'new',
    priority: 'high',
    category: 'efficiency',
    createdAt: '2026-01-18T10:00:00Z',
    affectedWorkflows: ['Password Reset', 'User Support'],
    proposedAction: 'Create self-service password reset workflow with MFA verification'
  },
  {
    id: 'sug_002',
    type: 'security',
    title: 'Consolidate admin access patterns',
    description: '8 users have elevated production access but only use 2 of 6 granted permissions. Consider role-based access reduction.',
    impact: {
      riskReduction: 'High',
      usersAffected: 8,
      permissionsToRemove: 32
    },
    confidence: 87,
    evidence: [
      '8 users with prod-admin role',
      'Only 2 permissions actively used in 90 days',
      '4 unused permissions per user on average',
      'Least privilege principle violation'
    ],
    status: 'new',
    priority: 'high',
    category: 'security',
    createdAt: '2026-01-17T14:30:00Z',
    affectedWorkflows: ['Access Management', 'Compliance'],
    proposedAction: 'Create right-sized prod-admin-lite role with only required permissions'
  },
  {
    id: 'sug_003',
    type: 'optimization',
    title: 'Batch software license requests',
    description: 'License approval requests spike on Mondays. Batching into daily approval digest could reduce approver burden by 60%.',
    impact: {
      timeSaved: '3 hours/week',
      approvalsSaved: 45,
      costSavings: '$600/month'
    },
    confidence: 91,
    evidence: [
      '68% of license requests submitted Mon-Tue',
      'Average 15 requests per day requiring approval',
      'Same approver handling 80% of requests',
      'Batch approval already used for similar workflows'
    ],
    status: 'reviewing',
    priority: 'medium',
    category: 'efficiency',
    createdAt: '2026-01-16T09:15:00Z',
    affectedWorkflows: ['Software Requests', 'License Management'],
    proposedAction: 'Enable daily digest mode for license approval workflow',
    reviewer: 'Sarah Kim'
  },
  {
    id: 'sug_004',
    type: 'automation',
    title: 'Auto-approve low-risk access requests',
    description: 'Read-only access to shared drives can be auto-approved for users in same department based on peer comparison.',
    impact: {
      timeSaved: '8 hours/week',
      ticketsAffected: 32,
      costSavings: '$1,600/month'
    },
    confidence: 89,
    evidence: [
      '32 shared drive access requests last month',
      '100% approval rate for read-only requests',
      'Average wait time: 4 hours for approval',
      'Peer group already has access in 90% of cases'
    ],
    status: 'accepted',
    priority: 'medium',
    category: 'efficiency',
    createdAt: '2026-01-15T11:00:00Z',
    affectedWorkflows: ['Access Requests', 'File Sharing'],
    proposedAction: 'Add auto-approval rule for read-only shared drive access within department',
    acceptedAt: '2026-01-16T10:00:00Z',
    acceptedBy: 'Mike Lee'
  },
  {
    id: 'sug_005',
    type: 'process',
    title: 'Standardize onboarding checklists by role',
    description: 'Engineering onboarding takes 40% longer than average. Role-specific templates could reduce provisioning time.',
    impact: {
      timeSaved: '2 hours/onboard',
      ticketsAffected: 12,
      costSavings: '$480/month'
    },
    confidence: 78,
    evidence: [
      'Engineering onboarding: avg 6 hours',
      'Other departments: avg 3.5 hours',
      '12 engineering hires in last 30 days',
      'Common steps identified across all engineering onboards'
    ],
    status: 'dismissed',
    priority: 'low',
    category: 'efficiency',
    createdAt: '2026-01-14T16:45:00Z',
    affectedWorkflows: ['Onboarding', 'Identity'],
    proposedAction: 'Create engineering-specific onboarding template with pre-defined access bundles',
    dismissedAt: '2026-01-15T09:00:00Z',
    dismissedBy: 'Jane Doe',
    dismissReason: 'Engineering onboarding already has custom checklist, issue is manual approval delays'
  },
  {
    id: 'sug_006',
    type: 'cost',
    title: 'Reclaim unused SaaS licenses',
    description: '23 Figma licenses show no activity in 60+ days. Reclaiming could save $2,760 annually.',
    impact: {
      costSavings: '$230/month',
      licensesToReclaim: 23,
      utilizationImprovement: '28%'
    },
    confidence: 96,
    evidence: [
      '23 Figma licenses with no login > 60 days',
      'Current license cost: $10/user/month',
      'Users confirmed moved to different roles',
      'No pending projects requiring access'
    ],
    status: 'new',
    priority: 'medium',
    category: 'cost',
    createdAt: '2026-01-18T08:00:00Z',
    affectedWorkflows: ['License Management', 'SaaS'],
    proposedAction: 'Send reclamation notice to users, auto-revoke after 7 days without response'
  }
];

const typeConfig = {
  automation: { icon: Zap, color: 'text-primary', bg: 'bg-primary/10', label: 'Automation' },
  security: { icon: Shield, color: 'text-slate-700', bg: 'bg-slate-100', label: 'Security' },
  optimization: { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Optimization' },
  process: { icon: RefreshCw, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Process' },
  cost: { icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50', label: 'Cost Savings' }
};

const statusConfig = {
  new: { label: 'New', className: 'bg-primary/10 text-primary' },
  reviewing: { label: 'Under Review', className: 'bg-slate-200 text-slate-700' },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-700' },
  dismissed: { label: 'Dismissed', className: 'bg-slate-100 text-slate-500' },
  implemented: { label: 'Implemented', className: 'bg-primary text-white' }
};

const SuggestionDetailModal = ({ suggestion, onClose, onAccept, onDismiss }) => {
  const [dismissReason, setDismissReason] = useState('');
  const [showDismissForm, setShowDismissForm] = useState(false);

  if (!suggestion) return null;

  const TypeIcon = typeConfig[suggestion.type]?.icon || Lightbulb;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig[suggestion.type]?.bg}`}>
              <TypeIcon className={`w-5 h-5 ${typeConfig[suggestion.type]?.color}`} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">{suggestion.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded ${statusConfig[suggestion.status]?.className}`}>
                  {statusConfig[suggestion.status]?.label}
                </span>
                <span className="text-xs text-slate-500">{suggestion.confidence}% confidence</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <p className="text-sm text-slate-700">{suggestion.description}</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {suggestion.impact.timeSaved && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">Time Saved</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">{suggestion.impact.timeSaved}</div>
              </div>
            )}
            {suggestion.impact.costSavings && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">Cost Savings</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">{suggestion.impact.costSavings}</div>
              </div>
            )}
            {suggestion.impact.ticketsAffected && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">Tickets Affected</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">{suggestion.impact.ticketsAffected}</div>
              </div>
            )}
            {suggestion.impact.riskReduction && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500">Risk Reduction</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">{suggestion.impact.riskReduction}</div>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Evidence</h3>
            <ul className="space-y-1.5">
              {suggestion.evidence.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-primary/5 rounded-lg p-3 border border-primary/10">
            <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-primary" />
              Proposed Action
            </h3>
            <p className="text-sm text-slate-700">{suggestion.proposedAction}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Affected Workflows</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {suggestion.affectedWorkflows.map((wf) => (
                <span key={wf} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                  {wf}
                </span>
              ))}
            </div>
          </div>

          {suggestion.status === 'dismissed' && suggestion.dismissReason && (
            <div className="bg-slate-50 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-1">Dismiss Reason</h3>
              <p className="text-sm text-slate-600">{suggestion.dismissReason}</p>
              <p className="text-xs text-slate-400 mt-1">By {suggestion.dismissedBy} on {suggestion.dismissedAt}</p>
            </div>
          )}

          {suggestion.status === 'accepted' && (
            <div className="bg-green-50 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-green-700 mb-1">Accepted</h3>
              <p className="text-xs text-green-600">By {suggestion.acceptedBy} on {suggestion.acceptedAt}</p>
            </div>
          )}

          {showDismissForm && (
            <div className="border border-slate-200 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Why are you dismissing this suggestion?</h3>
              <textarea
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                placeholder="Explain why this suggestion isn't applicable..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => setShowDismissForm(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-700"
                  onClick={() => {
                    onDismiss(suggestion.id, dismissReason);
                    setShowDismissForm(false);
                  }}
                  disabled={!dismissReason}
                >
                  Confirm Dismiss
                </Button>
              </div>
            </div>
          )}
        </div>

        {suggestion.status === 'new' && !showDismissForm && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500"
              onClick={() => setShowDismissForm(true)}
            >
              <ThumbsDown className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Bookmark className="w-4 h-4 mr-1" />
                Save for Later
              </Button>
              <Button size="sm" onClick={() => onAccept(suggestion.id)}>
                <ThumbsUp className="w-4 h-4 mr-1" />
                Accept & Implement
              </Button>
            </div>
          </div>
        )}

        {suggestion.status === 'accepted' && (
          <div className="flex items-center justify-end px-4 py-3 border-t border-slate-200">
            <Button size="sm">
              <Play className="w-4 h-4 mr-1" />
              View Implementation
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Suggestions = () => {
  const [suggestions, setSuggestions] = useState(mockSuggestions);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const filteredSuggestions = useMemo(() => {
    return suggestions.filter(s => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
      return true;
    });
  }, [suggestions, statusFilter, categoryFilter]);

  const stats = useMemo(() => ({
    new: suggestions.filter(s => s.status === 'new').length,
    reviewing: suggestions.filter(s => s.status === 'reviewing').length,
    accepted: suggestions.filter(s => s.status === 'accepted').length,
    totalSavings: suggestions
      .filter(s => s.status === 'accepted' && s.impact.costSavings)
      .reduce((acc, s) => {
        const match = s.impact.costSavings.match(/\$([0-9,]+)/);
        return acc + (match ? parseInt(match[1].replace(',', '')) : 0);
      }, 0)
  }), [suggestions]);

  const handleAccept = (id) => {
    setSuggestions(suggestions.map(s =>
      s.id === id
        ? { ...s, status: 'accepted', acceptedAt: new Date().toISOString(), acceptedBy: 'Current User' }
        : s
    ));
    setSelectedSuggestion(null);
  };

  const handleDismiss = (id, reason) => {
    setSuggestions(suggestions.map(s =>
      s.id === id
        ? { ...s, status: 'dismissed', dismissedAt: new Date().toISOString(), dismissedBy: 'Current User', dismissReason: reason }
        : s
    ));
    setSelectedSuggestion(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-slate-500" />
            Suggestions
          </h1>
          <p className="text-sm text-slate-500 mt-1">AI-generated recommendations based on observed patterns</p>
        </div>
        <Button variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh Analysis
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">New Suggestions</span>
            <Lightbulb className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.new}</div>
          <div className="text-xs text-slate-400 mt-1">awaiting review</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Under Review</span>
            <Eye className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.reviewing}</div>
          <div className="text-xs text-slate-400 mt-1">being evaluated</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Accepted</span>
            <Check className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.accepted}</div>
          <div className="text-xs text-slate-400 mt-1">implemented</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Est. Savings</span>
            <DollarSign className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-2xl font-semibold text-green-600">${stats.totalSavings.toLocaleString()}</div>
          <div className="text-xs text-slate-400 mt-1">per month from accepted</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {['all', 'new', 'reviewing', 'accepted', 'dismissed'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-2 py-1 text-xs font-medium rounded capitalize transition-colors ${
                statusFilter === status ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {status === 'all' ? 'All' : statusConfig[status]?.label || status}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="all">All Categories</option>
            <option value="efficiency">Efficiency</option>
            <option value="security">Security</option>
            <option value="cost">Cost Savings</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="divide-y divide-slate-100">
          {filteredSuggestions.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">
              No suggestions match your filters
            </div>
          ) : (
            filteredSuggestions.map((suggestion) => {
              const TypeIcon = typeConfig[suggestion.type]?.icon || Lightbulb;
              return (
                <div
                  key={suggestion.id}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedSuggestion(suggestion)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig[suggestion.type]?.bg}`}>
                    <TypeIcon className={`w-5 h-5 ${typeConfig[suggestion.type]?.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${statusConfig[suggestion.status]?.className}`}>
                        {statusConfig[suggestion.status]?.label}
                      </span>
                      <span className="text-sm font-medium text-slate-900">{suggestion.title}</span>
                      {suggestion.priority === 'high' && (
                        <AlertTriangle className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{suggestion.description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-slate-400">{suggestion.confidence}% confidence</span>
                      {suggestion.impact.timeSaved && (
                        <span className="text-xs text-primary flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {suggestion.impact.timeSaved}
                        </span>
                      )}
                      {suggestion.impact.costSavings && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {suggestion.impact.costSavings}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              );
            })
          )}
        </div>
      </div>

      <SuggestionDetailModal
        suggestion={selectedSuggestion}
        onClose={() => setSelectedSuggestion(null)}
        onAccept={handleAccept}
        onDismiss={handleDismiss}
      />
    </div>
  );
};

export default Suggestions;
